import { Spin } from 'antd';
import clsx from 'clsx';
import React from 'react';

import s from './Preview.module.less';

type SpinProps = React.ComponentProps<typeof Spin>;

// `OverlaySpinner = styled(Spin)` → thin FC wrapping antd `Spin`; forwards
// `className` (Pattern 3) so the static `.overlaySpinner` positioning class
// resolves. Static-only migration (Plan 02-05); no prop branches.
export const OverlaySpinner: React.FC<SpinProps> = (props) => {
  const { className, ...rest } = props;

  return <Spin {...rest} className={clsx(s.overlaySpinner, className)} />;
};

// `imgSize` (continuous, e.g. "60px" — or the unit-less "80" landmine) drives
// the `--img-size` CSS var via inline style; classes read `var(--img-size, 90px)`
// reproducing the emotion `${imgSize || '90px'}` fallback. LANDMINE: a caller
// passing imgSize="80" (CreateCanvasFiles/File.tsx) yields `--img-size:80` →
// `width:80` (no unit → invalid → ignored) → current behavior preserved. NOT
// normalized to 80px. Flagged for STY-02 visual review. (Plan 03-04, MIG-04.)
const imgSizeStyle = (imgSize?: string): React.CSSProperties | undefined =>
  imgSize ? ({ '--img-size': imgSize } as React.CSSProperties) : undefined;

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  isCleaned?: boolean;
  isSelected?: boolean;
  imgSize?: string;
  ignoreCleaned?: boolean;
}

// `PreviewItemDiv = styled.div<Props>` → booleans drive clsx classes; imgSize
// drives --img-size. The `.selected > span` border uses !important (Pitfall 6);
// `> div` border is non-important. Forwards className + ...rest (cross-file
// consumers — MIG-05).
export const PreviewItemDiv: React.FC<Props> = (props) => {
  const { isCleaned, isSelected, imgSize, ignoreCleaned, className, children, ...rest } = props;

  return (
    <div
      {...rest}
      style={imgSizeStyle(imgSize)}
      className={clsx(s.previewItem, className, {
        [s.notCleaned]: !isCleaned && !ignoreCleaned,
        [s.selected]: isSelected,
      })}
    >
      {children}
    </div>
  );
};

interface NotCleanedProps extends React.HTMLAttributes<HTMLSpanElement> {
  imgSize?: string;
}

// `NotCleaned = styled.span<NotCleanedProps>` → width = var(--img-size, 90px).
export const NotCleaned: React.FC<NotCleanedProps> = (props) => {
  const { imgSize, className, children, ...rest } = props;

  return (
    <span {...rest} style={imgSizeStyle(imgSize)} className={clsx(s.notCleanedBadge, className)}>
      {children}
    </span>
  );
};

interface PreviewDivProps extends React.HTMLAttributes<HTMLDivElement> {
  imgSize?: string;
}

// `PreviewDiv = styled.div<PreviewDivProps>` → imgSize present toggles the
// horizontal/vertical spacing of `> div` children (margin-right vs -bottom).
export const PreviewDiv: React.FC<PreviewDivProps> = (props) => {
  const { imgSize, className, children, ...rest } = props;

  return (
    <div {...rest} className={clsx(s.previewDiv, className, { [s.hasImgSize]: imgSize })}>
      {children}
    </div>
  );
};

// `DivImg = styled.div<PreviewDivProps>` → width/height/min-* = var(--img-size,
// 90px); imgSize present hides `.no-preview-text` (gated via .hasImgSize).
export const DivImg: React.FC<PreviewDivProps> = (props) => {
  const { imgSize, className, children, ...rest } = props;

  return (
    <div {...rest} style={imgSizeStyle(imgSize)} className={clsx(s.divImg, className, { [s.hasImgSize]: imgSize })}>
      {children}
    </div>
  );
};

interface PreviewShowMoreProps extends React.HTMLAttributes<HTMLDivElement> {
  imgSize?: string;
}

// `PreviewShowMore = styled.div<PreviewShowMoreProps>` → width/height =
// var(--img-size, 90px); imgSize present sets `button { font-size:10px }`.
export const PreviewShowMore: React.FC<PreviewShowMoreProps> = (props) => {
  const { imgSize, className, children, ...rest } = props;

  return (
    <div
      {...rest}
      style={imgSizeStyle(imgSize)}
      className={clsx(s.previewShowMore, className, { [s.hasImgSize]: imgSize })}
    >
      {children}
    </div>
  );
};
